import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from '@environment/environment';
import {
  IAnnouncementsPage
} from '@shared/interfaces/announcement.interface';
import { Announcement } from '@shared/models/announcement.model';
import { LoggerService } from '@shared/services/logger.service';

@Injectable({
  providedIn: 'root'
})
export class AnnouncementsService implements OnDestroy {
  private static readonly POLL_MS = 5 * 60 * 1000;

  private readonly ENDPOINT_V3: string = String(
    `${environment.ENDPOINTS.BACK_END.V3}/announcements`
  );

  private unreadCountSource = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSource.asObservable();

  private sidebarOpenSource = new BehaviorSubject<boolean>(false);
  public sidebarOpen$ = this.sidebarOpenSource.asObservable();

  private pollSub?: Subscription;

  constructor(
    private readonly _http: HttpClient,
    private readonly _logger: LoggerService
  ) {}

  public ngOnDestroy(): void {
    this.stopPolling();
  }

  public startPolling(): void {
    if (this.pollSub) {
      return;
    }
    this.pollSub = timer(0, AnnouncementsService.POLL_MS).subscribe(() =>
      this.refreshUnreadCount()
    );
  }

  public stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  public openSidebar(): void {
    this.sidebarOpenSource.next(true);
  }

  public closeSidebar(): void {
    this.sidebarOpenSource.next(false);
  }

  public toggleSidebar(): void {
    this.sidebarOpenSource.next(!this.sidebarOpenSource.value);
  }

  public refreshUnreadCount(): void {
    this._http
      .get<{ count: number }>(`${this.ENDPOINT_V3}/unread_count`)
      .subscribe({
        next: (res) => this.unreadCountSource.next(res.count),
        error: (err) =>
          this._logger.error('AnnouncementsService', 'unread_count', err)
      });
  }

  public getAnnouncements(page: number = 1): Observable<IAnnouncementsPage> {
    const params = new HttpParams()
      .set('page[number]', String(page))
      .set('page[page_size]', '10');

    return this._http
      .get(this.ENDPOINT_V3, { params })
      .pipe(map((res: any) => Announcement.pageFromAPI(res)));
  }

  public markRead(id: string): Observable<void> {
    return this._http.post<void>(`${this.ENDPOINT_V3}/${id}/read`, {}).pipe(
      tap(() =>
        this.unreadCountSource.next(Math.max(0, this.unreadCountSource.value - 1))
      )
    );
  }

  public markAllRead(): Observable<void> {
    return this._http
      .post<void>(`${this.ENDPOINT_V3}/read_all`, {})
      .pipe(tap(() => this.unreadCountSource.next(0)));
  }
}
